import { Link } from 'react-router-dom';
import { BookCover } from '@/components/workbooks/BookCover';
import { workbooks } from '@/data/workbooks';
import { COST_INDEX_EDITION, firstYearTotal, lifestyleKeys, lifestyleName } from '@/data/costIndex';

const usd = (n: number) => `$${n.toLocaleString('en-US')}`;

export const ToolkitSection = () => {
  const featured = workbooks.slice(0, 3);

  return (
    <section className="py-16 md:py-24" aria-labelledby="toolkit-heading">
      <div className="container mx-auto px-6 md:px-8">
        <header className="max-w-3xl mb-12 md:mb-16">
          <p className="text-[10px] tracking-luxury uppercase text-accent/80 mb-4">
            Beyond the guides
          </p>
          <h2
            id="toolkit-heading"
            className="font-serif text-2xl md:text-4xl font-medium text-foreground leading-tight"
          >
            The numbers, the calculators and the workbooks
          </h2>
          <p className="mt-5 text-sm md:text-base text-muted-foreground/90 font-light leading-relaxed">
            Reading is the easy part. These three are what people actually come back to when the
            decision gets close.
          </p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10 md:gap-12">
          {/* Cost index */}
          <article className="border-t border-border/40 pt-6">
            <p className="text-[10px] tracking-luxury uppercase text-accent/80">
              Edition {COST_INDEX_EDITION}
            </p>
            <h3 className="font-serif text-xl md:text-2xl text-foreground mt-2 leading-snug">
              The Nomad Cost Index
            </h3>
            <p className="mt-3 text-sm text-muted-foreground/90 font-light leading-relaxed">
              Our own compilation of what each way of living costs, priced on the same terms so the
              four columns can actually be compared.
            </p>
            <dl className="mt-5 space-y-2">
              {lifestyleKeys.map((key) => (
                <div key={key} className="flex items-baseline justify-between gap-4">
                  <dt className="text-sm font-light text-muted-foreground/85">
                    {lifestyleName[key]}
                  </dt>
                  <dd className="font-serif text-base text-foreground">
                    {usd(firstYearTotal(key))}
                    <span className="ml-1 text-[10px] tracking-luxury uppercase text-muted-foreground">
                      yr 1
                    </span>
                  </dd>
                </div>
              ))}
            </dl>
            <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2">
              <Link
                to="/cost-index"
                className="inline-flex items-center min-h-[44px] text-[10px] tracking-luxury uppercase text-accent hover:text-accent/70 transition-colors duration-500"
              >
                Read the index
              </Link>
              <Link
                to="/cost-index#methodology"
                className="inline-flex items-center min-h-[44px] text-[10px] tracking-luxury uppercase text-muted-foreground/80 hover:text-accent transition-colors duration-500"
              >
                How we calculate this
              </Link>
            </div>
          </article>

          {/* Tools */}
          <article className="border-t border-border/40 pt-6">
            <p className="text-[10px] tracking-luxury uppercase text-accent/80">Four calculators</p>
            <h3 className="font-serif text-xl md:text-2xl text-foreground mt-2 leading-snug">
              Put your own figures in
            </h3>
            <p className="mt-3 text-sm text-muted-foreground/90 font-light leading-relaxed">
              Swap every line of the index for your rent, your payload, your mooring quote — then
              see the month the maths turns in your favour.
            </p>
            <ul className="mt-5 space-y-2">
              {[
                { label: 'Cost comparison', tab: 'compare' },
                { label: 'Budget planner', tab: 'budget' },
                { label: 'Equipment calculator', tab: 'equipment' },
                { label: 'Break-even point', tab: 'breakeven' },
              ].map((t) => (
                <li key={t.tab}>
                  <Link
                    to={`/tools?tab=${t.tab}`}
                    className="inline-flex items-center min-h-[40px] text-sm font-light text-muted-foreground/85 hover:text-accent transition-colors duration-500"
                  >
                    {t.label}
                  </Link>
                </li>
              ))}
            </ul>
          </article>

          {/* Workbooks */}
          <article className="group border-t border-border/40 pt-6">
            <p className="text-[10px] tracking-luxury uppercase text-accent/80">
              The workbook collection
            </p>
            <h3 className="font-serif text-xl md:text-2xl text-foreground mt-2 leading-snug">
              Seven printed planning documents
            </h3>
            <p className="mt-3 text-sm text-muted-foreground/90 font-light leading-relaxed">
              Written to be filled in by hand, in sequence, until the decision is on paper and
              dated.
            </p>
            <div className="mt-6 flex items-end">
              {featured.map((w, i) => (
                <div
                  key={w.slug}
                  className="transition-transform duration-500"
                  style={{
                    marginLeft: i === 0 ? 0 : '-24px',
                    transform: `rotate(${(i - 1) * 4}deg)`,
                    zIndex: i,
                  }}
                >
                  <BookCover title={w.title} tone={w.coverTone} size="sm" hover={false} />
                </div>
              ))}
            </div>
            <Link
              to="/workbooks"
              className="inline-flex items-center min-h-[44px] mt-4 text-[10px] tracking-luxury uppercase text-accent hover:text-accent/70 transition-colors duration-500"
            >
              See the collection
            </Link>
          </article>
        </div>
      </div>
    </section>
  );
};
